import { useMemo } from 'react';

import { useSession } from '@/shared/session';

export type MerchantPortalNavFeatureKey =
  | 'store'
  | 'team'
  | 'orders'
  | 'customers'
  | 'media'
  | 'pricing'
  | 'loyalty';

const NAV_FEATURE_FLAG_KEYS: Record<MerchantPortalNavFeatureKey, string> = {
  store: 'merchantPortal.nav.store',
  team: 'merchantPortal.nav.team',
  orders: 'merchantPortal.nav.orders',
  customers: 'merchantPortal.nav.customers',
  media: 'merchantPortal.nav.media',
  pricing: 'merchantPortal.nav.pricing',
  loyalty: 'merchantPortal.nav.loyalty',
};

const DEFAULT_NAV_VISIBILITY: Record<MerchantPortalNavFeatureKey, boolean> = {
  store: true,
  team: false,
  orders: false,
  customers: false,
  media: false,
  pricing: false,
  loyalty: false,
};

export type MerchantPortalSettingsSectionKey =
  | 'ai'
  | 'salesChannels'
  | 'customersMarketing'
  | 'selling'
  | 'fulfilment'
  | 'catalogMedia';

const SETTINGS_SECTION_FLAG_KEYS: Record<MerchantPortalSettingsSectionKey, string> = {
  ai: 'merchantPortal.settings.ai',
  salesChannels: 'merchantPortal.settings.salesChannels',
  customersMarketing: 'merchantPortal.settings.customersMarketing',
  selling: 'merchantPortal.settings.selling',
  fulfilment: 'merchantPortal.settings.fulfilment',
  catalogMedia: 'merchantPortal.settings.catalogMedia',
};

const DEFAULT_SETTINGS_SECTION_VISIBILITY: Record<MerchantPortalSettingsSectionKey, boolean> = {
  ai: false,
  salesChannels: true,
  customersMarketing: false,
  selling: false,
  fulfilment: false,
  catalogMedia: false,
};

function resolveFeatureFlag(
  featureFlags: Record<string, boolean> | null | undefined,
  key: MerchantPortalNavFeatureKey,
): boolean {
  const value = featureFlags?.[NAV_FEATURE_FLAG_KEYS[key]];
  return typeof value === 'boolean' ? value : DEFAULT_NAV_VISIBILITY[key];
}

/**
 * Central switch for sidebar nav visibility.
 *
 * The hidden sections default to off, but each one can be turned back on via a
 * session-provided `featureFlags` entry. That keeps the menu easy to gate now
 * and easy to externalise later without touching the shell again.
 */
export function resolveMerchantPortalNavVisibility(
  featureFlags?: Record<string, boolean> | null,
): Record<MerchantPortalNavFeatureKey, boolean> {
  return {
    store: resolveFeatureFlag(featureFlags, 'store'),
    team: resolveFeatureFlag(featureFlags, 'team'),
    orders: resolveFeatureFlag(featureFlags, 'orders'),
    customers: resolveFeatureFlag(featureFlags, 'customers'),
    media: resolveFeatureFlag(featureFlags, 'media'),
    pricing: resolveFeatureFlag(featureFlags, 'pricing'),
    loyalty: resolveFeatureFlag(featureFlags, 'loyalty'),
  };
}

/** Reads the current sidebar visibility toggles from the session projection. */
export function useMerchantPortalNavVisibility(): Record<MerchantPortalNavFeatureKey, boolean> {
  const { featureFlags } = useSession();
  return useMemo(() => resolveMerchantPortalNavVisibility(featureFlags), [featureFlags]);
}

function resolveSettingsSectionFlag(
  featureFlags: Record<string, boolean> | null | undefined,
  key: MerchantPortalSettingsSectionKey,
): boolean {
  const value = featureFlags?.[SETTINGS_SECTION_FLAG_KEYS[key]];
  return typeof value === 'boolean' ? value : DEFAULT_SETTINGS_SECTION_VISIBILITY[key];
}

/** Reads the current settings-section visibility toggles from the session projection. */
export function resolveMerchantPortalSettingsSectionVisibility(
  featureFlags?: Record<string, boolean> | null,
): Record<MerchantPortalSettingsSectionKey, boolean> {
  return {
    ai: resolveSettingsSectionFlag(featureFlags, 'ai'),
    salesChannels: resolveSettingsSectionFlag(featureFlags, 'salesChannels'),
    customersMarketing: resolveSettingsSectionFlag(featureFlags, 'customersMarketing'),
    selling: resolveSettingsSectionFlag(featureFlags, 'selling'),
    fulfilment: resolveSettingsSectionFlag(featureFlags, 'fulfilment'),
    catalogMedia: resolveSettingsSectionFlag(featureFlags, 'catalogMedia'),
  };
}

/** Reads the current settings-section visibility toggles from the session projection. */
export function useMerchantPortalSettingsSectionVisibility(): Record<
  MerchantPortalSettingsSectionKey,
  boolean
> {
  const { featureFlags } = useSession();
  return useMemo(() => resolveMerchantPortalSettingsSectionVisibility(featureFlags), [featureFlags]);
}
